import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { auth, ApiClientError } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import { Alert } from '../components/ui';

/**
 * The email-driven auth flows: verify-email (landing from the email link),
 * forgot-password, and reset-password. Kept in one file — they share the
 * auth-card shell and are each a single small state machine.
 */

export function VerifyEmail() {
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const token = params.get('token');

  const [state, setState] = useState<'working' | 'done' | 'error'>('working');
  const [message, setMessage] = useState('Verifying your email…');

  useEffect(() => {
    if (!token) {
      setState('error');
      setMessage('This link is missing its token. Use the button in your email.');
      return;
    }

    auth
      .verifyEmail(token)
      .then(async () => {
        setState('done');
        setMessage('Your email is verified — you have full access now.');
        await refresh().catch(() => undefined);
      })
      .catch((error) => {
        setState('error');
        setMessage(
          error instanceof ApiClientError ? error.message : 'Verification failed — try again.'
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <h1>Email verification</h1>
        <p className="subtitle">{message}</p>
        {state !== 'working' && (
          <Link className="btn btn-primary" to="/">
            Go to dashboard
          </Link>
        )}
      </div>
    </div>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await auth.forgotPassword(email);
    } finally {
      // Always show the same outcome — no account enumeration from the UI.
      setSent(true);
      setBusy(false);
    }
  }

  return (
    <div className="auth-shell">
      <form className="auth-card" onSubmit={onSubmit}>
        <h1>Forgot password</h1>
        <p className="subtitle">We'll email you a reset link.</p>

        {sent ? (
          <>
            <Alert tone="success">
              If that address has an account, a reset link is on its way.
            </Alert>
            <Link to="/login">Back to sign in</Link>
          </>
        ) : (
          <>
            <label className="field">
              <span>Email</span>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                autoFocus
              />
            </label>
            <button className="btn btn-primary" style={{ width: '100%' }} disabled={busy}>
              {busy ? 'Sending…' : 'Send reset link'}
            </button>
            <p className="muted" style={{ marginTop: 16, marginBottom: 0 }}>
              <Link to="/login">Back to sign in</Link>
            </p>
          </>
        )}
      </form>
    </div>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');

  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!token) {
      setError('This link is missing its token. Use the button in your email.');
      return;
    }

    setError(null);
    setBusy(true);
    try {
      await auth.resetPassword(token, password);
      navigate('/login');
    } catch (caught) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not reset the password');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-shell">
      <form className="auth-card" onSubmit={onSubmit}>
        <h1>Choose a new password</h1>
        <p className="subtitle">At least 12 characters.</p>

        {error && <Alert>{error}</Alert>}

        <label className="field">
          <span>New password</span>
          <input
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="new-password"
            minLength={12}
            required
            autoFocus
          />
        </label>

        <button className="btn btn-primary" style={{ width: '100%' }} disabled={busy}>
          {busy ? 'Saving…' : 'Reset password'}
        </button>
      </form>
    </div>
  );
}
